import { Component, effect, OnDestroy, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject, finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { EnumValues, DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { ClientPurchaseOrderRoles } from '../../models/roles/roles-group';
import { ClientPurchaseOrder, ClientPurchaseOrderSearchCommand } from '../../models/client-purchase-order/client-purchase-order';
import { ClientPurchaseOrderProxyService } from '../../proxies/order/client-purchase-order.proxy.service';
import { EnumService } from '../../services/enum.service';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-client-purchase-orders',
  templateUrl: './client-purchase-orders.component.html',
  styleUrl: './client-purchase-orders.component.scss',
})
export class ClientPurchaseOrdersComponent implements OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translate = inject(TranslateService);
  private clientPOService = inject(ClientPurchaseOrderProxyService);

  isLoading = signal(0);
  listClientPOs = signal<ClientPurchaseOrder[]>([]);
  statuses = signal<EnumValues>({});
  types = signal<EnumValues>({});
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'client-purchase-order-filters', config: this.filterConfig });
  showFilters = signal(false);
  currentPage = signal<number>(1);
  totalRecords = signal<number>(0);
  pageSize = signal<number>(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  canEdit = computed(() => {
    return (
      AuthService.hasRole(ClientPurchaseOrderRoles.ClientPurchaseOrderAdmin) ||
      AuthService.hasRole(ClientPurchaseOrderRoles.ClientPurchaseOrderEditor)
    );
  });

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: ClientPurchaseOrderSearchCommand = {};

  getRowData(rowData: unknown): ClientPurchaseOrder {
    return rowData as ClientPurchaseOrder;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('ClientPOStatus', 'orderEnums').values;
        if (Object.keys(statusesValue).length > 0) {
          this.statuses.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const typesValue = this.enumService.getEnum('ClientPOType', 'orderEnums').values;
        if (Object.keys(typesValue).length > 0) {
          this.types.set(typesValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translate.currentLang && this.enumsReady()) {
          this.initializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = Object.entries(this.statuses()).map(([key, value]) => ({
      value: parseInt(key),
      label: value,
    }));

    const typeOptions = Object.entries(this.types()).map(([key, value]) => ({
      value: parseInt(key),
      label: value,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Client_PO_Number', 'CPO Number'),
          placeholder: translate('Client_PO_Number_Placeholder', 'Enter CPO number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('Client_PO_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'type',
          label: translate('Client_PO_Type', 'Type'),
          placeholder: translate('Select_type', 'Select type'),
          options: typeOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private onSearch(searchCriteria: ClientPurchaseOrderSearchCommand): void {
    this.startLoading();
    this.lastSearchCriteria = searchCriteria;
    this.clientPOService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const pagedResult = response.data;
          this.listClientPOs.set(pagedResult?.items ?? []);
          this.totalRecords.set(pagedResult?.totalCount ?? 0);
        },
        error: () => {
          this.listClientPOs.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = this.normalizeSortField(event.field, this.sortField());
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: ClientPurchaseOrderSearchCommand = {
      number: (filters['number'] as string)?.trim() || undefined,
      status:
        Array.isArray(filters['status']) && (filters['status'] as unknown[]).length > 0
          ? (filters['status'] as unknown[]).map((item: unknown) =>
              typeof item === 'object' && item !== null ? parseInt((item as Record<string, string>)['value']) : parseInt(String(item)),
            )
          : undefined,
      type:
        filters['type'] != null
          ? typeof filters['type'] === 'object'
            ? parseInt((filters['type'] as Record<string, string>)['value'])
            : parseInt(filters['type'] as string)
          : undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  public redirectToDetails(param?: string): void {
    this.router.navigate(['/client-purchase-order-details'], { queryParams: { id: param ?? 'new' } });
  }

  private normalizeSortField(sortField: string | string[] | undefined | null, fallback: string | undefined): string | undefined {
    if (Array.isArray(sortField)) {
      return sortField[0] ?? fallback;
    }
    return sortField ?? fallback;
  }
}
